/**
 * @file NewPropertiesTable.js
 * @description Properties panel for table container configuration.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import debounce from 'lodash/debounce';
import { FileDrop } from '../helpers/FileDrop';
import './PropertiesMultimedia.css';
import Toast from '../ui/toasts/Toast';
import { uploadFile } from '../../services/containerService';
import { useUploadContext } from '../../store/Contexts';
import {
    formatFileSize,
    getFileCategory,
    sanitizeFileName,
} from '../helpers/FileUtils';
import {
    createAsset,
    fetchAssets,
    fetchAssetByID,
} from '../../services/clientService';
import { InputField, SwitchField } from '../helpers/FormComponents';
import Tooltip from '../ui/tooltips/Tooltip';
import TagInput from '../helpers/TagInput';
import ContactsTable from '../contacts/Contacts';
import styles from '../plans/PlanYear.module.scss';
import assetStyles from '../assets/AssetLibrary.module.scss';
import AssetGridView from '../assets/components/AssetGridView';
import PaginationComponent from '../helpers/PaginationComponent';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import AddFromAssetsModal from '../ui/modals/AddFromAssetsModal';
import { isValidFileTypeAndSize } from '../../utils/validate';
import { Popover, Box } from '@mui/material';
import Spinner from '../ui/spinners/Spinner';
import { getText } from '../../i18n';

const SUPPORTED_TABLE_TYPES = ['xls', 'xlsx', 'csv'];

function NewPropertiesTable({
    item,
    setItem,
    handleUploadFromDevice,
    handleAddFromAssets,
    setIsSaving,
    tableState,
    setTableState,
}) {
    const [areInputsDisabled, setAreInputsDisabled] = useState(false);
    const [file, setFile] = useState(null);
    const { enqueueUpload } = useUploadContext();
    const [isAddFromAssetsChecked, setIsAddFromAssetsChecked] = useState('');
    const [isUploadFromDeviceChecked, setIsUploadFromDeviceChecked] =
        useState('');
    const [asset, setAsset] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [assets, setAssets] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeCard, setActiveCard] = useState(null);
    const [isInitialLoading, setIsInitialLoading] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [tableOpen, setTableOpen] = useState(false);
    const tableId = tableOpen ? 'simple-popover' : undefined;
    const [tableAnchorEl, setTableAnchorEl] = useState(null);
    const latestRequestIdRef = useRef(0);

    const { tableAsset, assetName, isInitialized } = tableState;

    function updateTableState(updates) {
        setTableState(prev => ({ ...prev, ...updates }));
    }

    function handleTablePopoverClick(event) {
        setTableAnchorEl(event.currentTarget);
        setTableOpen(!tableOpen);
    }

    function handlePopoverClose() {
        setTableAnchorEl(null);
        setTableOpen(false);
    }

    useEffect(() => {
        handleUploadFromDevice(isUploadFromDeviceChecked);
    }, [isUploadFromDeviceChecked, handleUploadFromDevice]);

    useEffect(() => {
        handleAddFromAssets(isAddFromAssetsChecked);
    }, [isAddFromAssetsChecked, handleAddFromAssets]);

    useEffect(() => {
        setItem(prevState => {
            const { contactsPart } = prevState;
            return {
                ...prevState,
                contactsPart: {
                    ...contactsPart,
                    data: '',
                },
                containerAssets: tableAsset.assetId ? [tableAsset] : [],
            };
        });
    }, [tableAsset, setItem]);

    function clearTable() {
        updateTableState({
            tableAsset: {},
            assetName: '',
            assetUrl: '',
            assetFileType: '',
        });
        setShowPreview(false);
    }

    function togglePreview() {
        setShowPreview(prev => !prev);
    }

    useEffect(() => {
        if (isInitialized) {
            return;
        }

        async function fetchInitial() {
            setIsInitialLoading(true);

            if (item === undefined) {
                updateTableState({ isInitialized: true });
                setIsInitialLoading(false);
                return;
            }

            if ('templateStatus' in item && item.templateStatus === -1) {
                setAreInputsDisabled(true);
            }

            let tempTableAsset = {};
            let tempAssetName = '';
            let tempAssetUrl = '';
            let tempAssetFileType = '';

            const hasContainerAssets =
                'containerAssets' in item &&
                item.containerAssets !== null &&
                item.containerAssets.length > 0;

            if (hasContainerAssets) {
                item.containerAssets = item.containerAssets.filter(
                    assetItem => Object.keys(assetItem).length > 0,
                );

                for (const containerAsset of item.containerAssets) {
                    if (!containerAsset.assetId) {
                        continue;
                    }

                    const fetchedAsset = await fetchAssetByID(
                        containerAsset.assetId,
                        null,
                    );

                    const fileCategory = getFileCategory(fetchedAsset.url);

                    if (fileCategory === 'spreadsheet') {
                        tempTableAsset = {
                            id: containerAsset.id,
                            assetId: containerAsset.assetId,
                        };
                        tempAssetName = fetchedAsset.name;
                        tempAssetUrl = fetchedAsset.url;
                        tempAssetFileType = fetchedAsset.fileType;
                        break;
                    }
                }
            }

            updateTableState({
                tableAsset: tempTableAsset,
                assetName: tempAssetName,
                assetUrl: tempAssetUrl,
                assetFileType: tempAssetFileType,
                isInitialized: true,
            });

            setIsInitialLoading(false);
        }

        fetchInitial();
    }, [isInitialized, item]);

    function handleFileChange(fileValue) {
        setFile(sanitizeFileName(fileValue));
    }

    async function uploadAsset() {
        setIsSaving(true);

        if (!validateForm()) {
            setIsSaving(false);
            return;
        }

        let urlResponse;
        let isValidUrl = false;

        if (!asset.url) {
            try {
                const response = await enqueueUpload(file.name, uploadOptions =>
                    uploadFile(file, uploadOptions),
                );
                if (response.error) {
                    Toast.error(response.message);
                    setIsSaving(false);
                    return;
                }
                urlResponse = encodeURI(response);
                try {
                    new URL(urlResponse);
                    isValidUrl = true;
                    setAsset(prev => ({
                        ...prev,
                        url: urlResponse,
                    }));
                } catch {
                    Toast.error(getText('msg_3e298875de25'));
                    setIsSaving(false);
                    return;
                }
            } catch {
                Toast.error(getText('msg_3e298875de25'));
                setIsSaving(false);
                return;
            }
        }

        if (!isValidUrl && !asset.url) {
            setIsSaving(false);
            return;
        }

        const newAsset = { ...asset };
        if (!newAsset.assetTypeId) {
            newAsset.assetTypeId = 4;
        }
        if (urlResponse) {
            newAsset.url = urlResponse;
        }
        newAsset.fileType = file.name.split('.').pop();
        newAsset.fileSize = formatFileSize(file.size);

        const createResponse = await createAsset(newAsset, null);

        if (createResponse.error) {
            Toast.error(createResponse.message);
            setIsSaving(false);
            return;
        }

        if (createResponse.status === 200 && createResponse.data.url) {
            Toast.success(getText('msg_9309271e92fe'));
            updateTableState({
                tableAsset: { assetId: createResponse.data.id },
                assetName: createResponse.data.name,
                assetUrl: createResponse.data.url,
                assetFileType: createResponse.data.fileType,
            });
            setIsUploadFromDeviceChecked('');
            setAsset({});
            setFile(null);
            setIsSaving(false);
            return;
        }

        Toast.error(getText('msg_9f8fd8f97fd3'));
        setIsSaving(false);
    }

    function validateForm() {
        if (!asset.name || asset.name.trim() === '') {
            Toast.error(getText('msg_d7659e2c4af2'));
            return false;
        }

        if (!file) {
            Toast.error(getText('msg_e11c5988bd25'));
            return false;
        }

        const fileTypeAndSizeValidation = isValidFileTypeAndSize(file);
        if (fileTypeAndSizeValidation) {
            Toast.error(fileTypeAndSizeValidation);
            return false;
        }

        if (asset.name.length > 256) {
            Toast.error(getText('msg_f4231db74e51'));
            return false;
        }

        return true;
    }

    function updateAsset(assetObj, key, value) {
        return { ...assetObj, [key]: value };
    }

    const loadAssets = useCallback(
        async (page = currentPage, search = searchTerm) => {
            latestRequestIdRef.current += 1;
            const requestId = latestRequestIdRef.current;

            setIsSearching(true);
            setIsSaving(true);

            try {
                let queryParams = {
                    assetTypes: [1, 2],
                    page,
                    pageSize: 12,
                    fileTypes: SUPPORTED_TABLE_TYPES,
                };

                if (search && search.trim() !== '') {
                    queryParams = { ...queryParams, search: search.trim() };
                }

                const fetchedAssets = await fetchAssets(
                    queryParams,
                    null,
                    false,
                );

                if (requestId !== latestRequestIdRef.current) {
                    return;
                }

                if (!fetchedAssets) {
                    setAssets([]);
                    setTotalPages(1);
                    return;
                }

                setAssets(fetchedAssets.viewModels || []);
                setTotalPages(fetchedAssets.pageTotal || 1);
            } catch (error) {
                console.error('Failed to fetch assets', error);
                Toast.error(getText('msg_cade149a85b2'));
            } finally {
                if (latestRequestIdRef.current === requestId) {
                    setIsSearching(false);
                    setIsSaving(false);
                }
            }
        },
        [currentPage, searchTerm, setIsSaving],
    );

    const debouncedLoadRef = useRef(
        debounce(term => {
            setCurrentPage(1);
            loadAssets(1, term);
        }, 400),
    );

    useEffect(() => {
        const debounced = debouncedLoadRef.current;
        return () => {
            debounced.cancel();
        };
    }, []);

    function handleSearch(event) {
        const value = event.target.value;
        setSearchTerm(value);
        debouncedLoadRef.current(value);

        if (value === '') {
            debouncedLoadRef.current.cancel();
            setCurrentPage(1);
            loadAssets(1, '');
        }
    }

    function handleSearchKeyDown(event) {
        if (event.key !== 'Enter') {
            return;
        }
        debouncedLoadRef.current.cancel();
        setCurrentPage(1);
        loadAssets(1, searchTerm);
    }

    useEffect(() => {
        if (isAddFromAssetsChecked) {
            loadAssets(currentPage, searchTerm);
        }
    }, [isAddFromAssetsChecked, currentPage]);

    function handleClick(index) {
        setActiveCard(activeCard === index ? null : index);
    }

    function addAsset() {
        if (activeCard === null) {
            Toast.error(
                getText('msg_c9d0d3fc9172'),
                null,
                getText('msg_40f0bdba0938'),
            );
            return;
        }

        const assetAdding = assets[activeCard];
        updateTableState({
            tableAsset: { assetId: assetAdding.id },
            assetName: assetAdding.name,
            assetUrl: assetAdding.url,
            assetFileType: assetAdding.fileType,
        });
        setIsAddFromAssetsChecked('');
        setSearchTerm('');
        setAssets([]);
        setTotalPages(1);
        setCurrentPage(1);
        setActiveCard(null);
        setShowPreview(false);
    }

    function handleAssetFromLibrarySelected(assetAdding) {
        updateTableState({
            tableAsset: { assetId: assetAdding.id },
            assetName: assetAdding.name,
            assetUrl: assetAdding.url,
            assetFileType: assetAdding.fileType,
        });
        setIsAddFromAssetsChecked('');
        setSearchTerm('');
        setAssets([]);
        setTotalPages(1);
        setCurrentPage(1);
        setActiveCard(null);
        setShowPreview(false);
    }

    function shouldHideForUploadOrAssets() {
        return isUploadFromDeviceChecked || isAddFromAssetsChecked;
    }

    if (isInitialLoading) {
        return <Spinner width={50} className={assetStyles.loadingSpinner} />;
    }

    return (
        <>
            <div
                style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    marginBottom: '20px',
                    display: shouldHideForUploadOrAssets() ? 'none' : 'block',
                }}
            >
                <div
                    style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: 'rgba(47, 43, 67, 1)',
                    }}
                >
                    {getText('msg_dcf4cc69bca4')}
                </div>
                <div
                    style={{
                        fontSize: '14px',
                        fontWeight: '400',
                        textAlign: 'left',
                        color: 'rgba(47, 43, 67, 0.6)',
                    }}
                >
                    {getText('msg_a76080d16f48')}
                </div>
            </div>

            <div
                style={{
                    display:
                        areInputsDisabled || shouldHideForUploadOrAssets()
                            ? 'none'
                            : 'block',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontSize: '16px',
                        fontWeight: '500',
                        color: 'rgba(47, 43, 67, 1)',
                        marginBottom: '10px',
                    }}
                >
                    <div>
                        {getText('msg_2c3cafa4db3f')}
                        <span
                            style={{
                                color: '#EF4E45',
                                marginLeft: '4px',
                                fontWeight: '500',
                            }}
                        >
                            *
                        </span>
                    </div>
                    <span
                        onClick={handleTablePopoverClick}
                        onKeyDown={e =>
                            e.key === 'Enter' && handleTablePopoverClick(e)
                        }
                        role="button"
                        tabIndex={0}
                        className="button-base ing-button-primary-left"
                    >
                        <i className="bi bi-plus-lg me-1" />
                        {getText('msg_61cc55aa0453')}
                    </span>
                    <Popover
                        id={tableId}
                        open={tableOpen}
                        anchorEl={tableAnchorEl}
                        onClose={handlePopoverClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'grid',
                                gap: 0,
                                paddingBottom: 0,
                                paddingTop: 0,
                                lineHeight: '40px',
                            }}
                        >
                            <div
                                onClick={() => {
                                    setIsAddFromAssetsChecked('table');
                                    setIsUploadFromDeviceChecked('');
                                    setTableOpen(false);
                                }}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        setIsAddFromAssetsChecked('table');
                                        setIsUploadFromDeviceChecked('');
                                        setTableOpen(false);
                                    }
                                }}
                                role="button"
                                tabIndex={0}
                                className="pointer-cursor profile-link"
                                style={{ paddingRight: '80px' }}
                            >
                                {getText('msg_0bc609667aa7')}
                            </div>
                            <div
                                onClick={() => {
                                    setIsAddFromAssetsChecked('');
                                    setIsUploadFromDeviceChecked('table');
                                    setTableOpen(false);
                                }}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        setIsAddFromAssetsChecked('');
                                        setIsUploadFromDeviceChecked('table');
                                        setTableOpen(false);
                                    }
                                }}
                                role="button"
                                tabIndex={0}
                                className="pointer-cursor profile-link"
                                style={{ paddingRight: '80px' }}
                            >
                                {getText('msg_c002fe6df29c')}
                            </div>
                        </Box>
                    </Popover>
                </div>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <div style={{ width: '100%' }}>
                        <div className={styles.fileBox}>
                            <div className={styles.fileName}>
                                {assetName || 'No file selected'}
                                {tableState.assetFileType &&
                                    `.${tableState.assetFileType}`}
                            </div>
                            {assetName && (
                                <div className={styles.fileActions}>
                                    <button
                                        type="button"
                                        className={styles.actionIcon}
                                        onClick={clearTable}
                                        title={getText('msg_304b14ba1a64')}
                                    >
                                        <i className="bi bi-trash3" />
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.actionIcon}
                                        onClick={togglePreview}
                                        title={getText('msg_420bc424b8f9')}
                                    >
                                        <i
                                            className={`bi ${showPreview ? 'bi-eye-slash' : 'bi-eye'}`}
                                        />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {showPreview && tableAsset.assetId && (
                    <div style={{ marginTop: 20 }}>
                        <ContactsTable
                            containerAssets={[tableAsset]}
                            title=""
                        />
                    </div>
                )}
            </div>

            {isUploadFromDeviceChecked && (
                <>
                    <div style={{ width: '100%', paddingTop: '6px' }}>
                        <FileDrop file={file} onChange={handleFileChange} />
                    </div>

                    <InputField
                        label={getText('msg_709a23220f2c')}
                        onChange={value =>
                            setAsset(updateAsset(asset, 'name', value))
                        }
                        maxChars={256}
                        required
                        placeholder={getText('msg_b7ffb9789f09')}
                        value={asset.name}
                    />

                    <div style={{ marginBottom: '24px' }}>
                        <div className="file-form-label">
                            {getText('msg_848eed0fbd54')}
                            <Tooltip
                                html="Use relevant and specific keywords to help make searching for an asset easier."
                                placement="right"
                            >
                                <i
                                    className="bi bi-info-circle"
                                    style={{
                                        paddingLeft: '5px',
                                        fontSize: '13px',
                                        color: 'var(--iNGAGED-Primary-Color)',
                                    }}
                                />
                            </Tooltip>
                        </div>
                        <TagInput
                            onTagsChange={tags =>
                                setAsset(updateAsset(asset, 'assetTags', tags))
                            }
                            savedTags={asset.tags}
                        />
                    </div>

                    <div style={{ marginBottom: '24px' }}>
                        <SwitchField
                            label={getText('msg_0c359a10ae17')}
                            onChange={isChecked =>
                                setAsset(
                                    updateAsset(
                                        asset,
                                        'assetTypeId',
                                        isChecked ? 4 : 5,
                                    ),
                                )
                            }
                            tooltip={getText('msg_5abd905257ee')}
                            checked={asset.assetTypeId !== 5}
                        />
                    </div>

                    <div
                        id="footer"
                        style={{
                            height: 'fit-content',
                            width: '100%',
                            padding: '28px',
                        }}
                    >
                        <div
                            role="button"
                            tabIndex={0}
                            style={{
                                height: '40px',
                                width: '128px',
                                textAlign: 'center',
                                display: 'inline-block',
                            }}
                            className="float-start button-base button-secondary"
                            onClick={() => setIsUploadFromDeviceChecked('')}
                            onKeyDown={e =>
                                e.key === 'Enter' &&
                                setIsUploadFromDeviceChecked('')
                            }
                        >
                            {getText('msg_77dfd2135f4d')}
                        </div>
                        <div
                            role="button"
                            tabIndex={0}
                            style={{
                                height: '40px',
                                width: '128px',
                                textAlign: 'center',
                                display: 'inline-block',
                            }}
                            className="float-end button-base ing-button-primary-left"
                            onClick={uploadAsset}
                            onKeyDown={e => e.key === 'Enter' && uploadAsset()}
                        >
                            {getText('msg_61cc55aa0453')}
                        </div>
                    </div>
                </>
            )}

            {isAddFromAssetsChecked && (
                <AddFromAssetsModal
                    title="Add from Library"
                    onYes={handleAssetFromLibrarySelected}
                    onNo={() => {
                        setIsAddFromAssetsChecked('');
                    }}
                    onHide={() => {
                        setIsAddFromAssetsChecked('');
                    }}
                    yesButtonText="Add"
                    noButtonText="Cancel"
                    acceptedFileTypes={SUPPORTED_TABLE_TYPES}
                    assetTypes={[1, 2]}
                    wasCreatedByMe={false}
                    displayModalBackdrop={false}
                />
            )}
        </>
    );
}

export default NewPropertiesTable;
